/**
 * FALCON — the two diagrams the R&D board carries, as of 2026-08-03.
 *
 * ONE high-level view and ONE process flow (Erez, 2026-08-03). The board used
 * to carry four Falcon boxes — two diagrams and two documents — which is more
 * surfaces than the thing has ideas. The documents now share one box; these are
 * the two pictures.
 *
 * 1 · THE PLATFORM   — what Falcon is made of, who touches it, and where the
 *                      one seam between our application and their agent
 *                      platform actually is.
 * 2 · THE TWO LOOPS  — the detail: how a conversation hands itself over when it
 *                      runs out of context, and how a write reaches an open
 *                      screen without anyone pressing refresh.
 *
 * Both are DATA objects on the house engine (`./c4`): nothing has a hand-written
 * width, a leaf measures its own text, a container measures its children, and
 * every wire resolves against the placed rects. Adding a component grows its
 * container, its parent and the canvas, and no edge moves off its anchor.
 *
 * They supersede FalconSystemDiagram (container view of the July build) and
 * ContextMemoryDiagram (the handover as designed, before it was built). Those
 * files stay — a board is the record — but nothing points at them any more.
 */
import { Canvas, leaf, person, box, type Diagram } from "./c4";

// ═══ 1 · THE PLATFORM ════════════════════════════════════════════════════════
//
// The question: what is this made of, and where does OUR software end and
// THEIRS begin? Everything is arranged to make one answer unmissable — the two
// systems meet at exactly one seam, an MCP server over our own API, and the
// client's business data never crosses into omnigent's database.

const PLATFORM: Diagram = (() => {
  const actors = box("fp-act", undefined, "col", [
    person("fp-dan", "Dan · broker"),
    person("fp-us", "Us · operator"),
  ], { pad: 0, gap: 34 });

  const doors = box("fp-doors", "DOORS", "col", [
    leaf("fp-tg", "Telegram", "[omni-falcon]", "Send"),
    leaf("fp-web", "Falcon console", "[RTL · Hebrew]", "LayoutDashboard"),
  ], { gap: 12 });

  // The console's six screens, split by who they belong to. The split is the
  // point: three are the business, three are the agent's own mind, and the
  // second group is the standard every later Tailormind app inherits.
  const screens = box("fp-screens", "SIX SCREENS", "col", [
    box("fp-biz", "The business", "row", [
      leaf("fp-prop", "Properties", undefined, "Building2"),
      leaf("fp-cli", "Clients", undefined, "Users"),
      leaf("fp-own", "Owners", undefined, "KeyRound"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("fp-mind", "The agent's mind", "row", [
      leaf("fp-mem", "Memory", undefined, "Brain"),
      leaf("fp-kb", "Knowledge", undefined, "BookOpen"),
      leaf("fp-skill", "Procedures", undefined, "ListChecks"),
    ], { depth: 2, gap: 10, pad: 10 }),
  ], { gap: 12 });

  const ours = box("fp-ours", "OUR APPLICATION — falcon-app", "col", [
    box("fp-doorsvc", "Two doors, one read layer", "row", [
      leaf("fp-api", "/api", "[the screens]", "Server"),
      leaf("fp-mcp", "/mcp", "[17 tools · bearer]", "Plug"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("fp-store", "The pages store", "row", [
      leaf("fp-pages", "pages", "[memory · kb · skill]", "FileText"),
      leaf("fp-ptr", "session pointer", "[which chat is live]", "Compass"),
    ], { depth: 2, gap: 10, pad: 10 }),
    leaf("fp-gate", "THE WRITE GATE", "[per-field provenance]", "ShieldCheck", "gate"),
    leaf("fp-fdb", "Falcon Postgres", "[30 business tables]", "Database"),
  ], { gap: 12 });

  const theirs = box("fp-theirs", "OMNIGENT — unmodified", "col", [
    leaf("fp-srv", "Server", "[sessions · auth]", "Boxes"),
    leaf("fp-run", "Runner + harness", "[claude-sdk]", "Cpu"),
    leaf("fp-odb", "omnigent Postgres", "[agent bookkeeping]", "Database"),
  ], { gap: 12 });

  const voice = box("fp-voice", "VOICE", "col", [
    leaf("fp-stt", "ivrit-whisper", "[Hebrew STT]", "AudioLines"),
    leaf("fp-tts", "Kokoro", "[Hebrew TTS]", "Volume2"),
  ], { gap: 12 });

  return {
    key: "fp",
    title: "Falcon — the platform",
    subtitle: "Two systems, one seam · the first Tailormind cloud application · v2.0 · 2026-08-03",
    padBottom: 84,
    cols: [
      { root: actors, align: "center" },
      { root: doors, align: "center" },
      { root: screens, align: "center" },
      { root: ours, align: "center" },
      { root: theirs, align: "center" },
      { root: voice, align: "center" },
    ],
    wires: [
      { a: "fp-dan", b: "fp-tg", as: "r", bs: "l", label: "voice + text" },
      { a: "fp-us", b: "fp-web", as: "r", bs: "l", label: "HTTPS" },
      { a: "fp-doors", b: "fp-screens", as: "r", bs: "l", label: "HTTPS" },
      { a: "fp-screens", b: "fp-api", as: "r", bs: "l", label: "HTTPS + SSE" },
      { a: "fp-run", b: "fp-mcp", as: "b", bs: "b", label: "MCP — THE ONE SEAM" },
      { a: "fp-mcp", b: "fp-gate", as: "b", bs: "t", label: "every write" },
      { a: "fp-gate", b: "fp-fdb", as: "b", bs: "t", label: "SQL", red: true },
      { a: "fp-web", b: "fp-srv", as: "r", bs: "l", label: "chat · WSS", dashed: true },
      { a: "fp-tg", b: "fp-voice", as: "b", bs: "b", label: "HTTP", dashed: true },
    ],
    legend: [
      { style: "solid", label: "request path" },
      { style: "dash", label: "the agent's own channels — chat and speech" },
      { style: "red", label: "the only path that writes Dan's data" },
    ],
  };
})();

// ═══ 2 · THE TWO LOOPS ═══════════════════════════════════════════════════════
//
// The detail, and the two things that are genuinely ours rather than omnigent's.
// Numbered discs are what make this a flow rather than a pipeline drawing: the
// order IS the content. Strip the numbers and both loops collapse into a row of
// boxes that could have been a list.

const LOOPS: Diagram = (() => {
  const stage = (id: string, title: string, kids: Parameters<typeof box>[3]) =>
    box(id, title, "col", kids, { depth: 1, gap: 12 });

  // LOOP A — the conversation runs out of room and hands itself over.
  const handover = box("fl-ho", "A · THE HANDOVER — when a conversation fills up", "row", [
    stage("fl-a1", "MEASURE", [
      leaf("fl-pol", "ceiling policy", "[inside omnigent]", "Gauge"),
    ]),
    stage("fl-a2", "ASK", [
      leaf("fl-inj", "hidden message", "[is_meta · mid-turn]", "EyeOff"),
    ]),
    stage("fl-a3", "SAVE", [
      leaf("fl-brief", "the agent writes", "[falcon_write_brief]", "PenLine"),
      leaf("fl-doc", "memory/handover", "[a document]", "FileText"),
    ]),
    stage("fl-a4", "ROLL", [
      leaf("fl-new", "fresh session", "[pointer moves]", "RefreshCw"),
    ]),
    stage("fl-a5", "CONTINUE", [
      leaf("fl-seed", "seeded with the note", "[hydrated, hidden]", "Sprout"),
      leaf("fl-follow", "both doors follow", "[poll · per message]", "Compass"),
    ]),
  ], { gap: 22 });

  // LOOP B — a write reaches an open screen without anyone pressing refresh.
  const live = box("fl-lv", "B · LIVE UPDATES — when anything writes", "row", [
    stage("fl-b1", "WRITE", [
      leaf("fl-w", "any writer", "[agent · psql · script]", "PenLine"),
    ]),
    stage("fl-b2", "NOTIFY", [
      leaf("fl-trg", "statement trigger", "[pg_notify]", "Zap"),
    ]),
    stage("fl-b3", "FAN OUT", [
      leaf("fl-lis", "one LISTEN", "[falcon-app]", "Ear"),
      leaf("fl-sse", "SSE", "[/api/events]", "Radio"),
    ]),
    stage("fl-b4", "INVALIDATE", [
      leaf("fl-inv", "invalidateQueries", "[TanStack Query]", "RotateCw"),
    ]),
    stage("fl-b5", "REFETCH", [
      leaf("fl-scr", "only open screens", "[normal /api route]", "LayoutDashboard"),
    ]),
  ], { gap: 22 });

  return {
    key: "fl",
    title: "Falcon — the two loops",
    subtitle: "How a conversation survives its own length, and how a screen stays true · v1.0 · 2026-08-03",
    padBottom: 84,
    cols: [
      { root: handover, align: "top" },
      { root: live, align: "top", dy: 0 },
    ],
    wires: [
      // A — numbered, because the order is the whole point.
      { a: "fl-pol", b: "fl-inj", as: "r", bs: "l", label: "over 100k", num: 1 },
      { a: "fl-inj", b: "fl-brief", as: "r", bs: "l", label: "into the LIVE turn", num: 2 },
      { a: "fl-brief", b: "fl-doc", as: "b", bs: "t", label: "in place", num: 3 },
      { a: "fl-doc", b: "fl-new", as: "r", bs: "l", label: "only after the save", num: 4, red: true },
      { a: "fl-new", b: "fl-seed", as: "r", bs: "l", label: "note inline", num: 5 },
      { a: "fl-seed", b: "fl-follow", as: "b", bs: "t", label: "console + Telegram" },
      // B — the same discipline on the shorter loop.
      { a: "fl-w", b: "fl-trg", as: "r", bs: "l", label: "SQL", num: 1 },
      { a: "fl-trg", b: "fl-lis", as: "r", bs: "l", label: "NOTIFY", num: 2 },
      { a: "fl-lis", b: "fl-sse", as: "b", bs: "t", label: "one connection", num: 3 },
      { a: "fl-sse", b: "fl-inv", as: "r", bs: "l", label: "text/event-stream", num: 4 },
      { a: "fl-inv", b: "fl-scr", as: "r", bs: "l", label: "HTTPS", num: 5 },
    ],
    legend: [
      { style: "solid", label: "ordered step — the numbers are the diagram" },
      { style: "red", label: "the pointer waits for the save; nothing else writes the note" },
    ],
  };
})();

/** High level: what Falcon is made of, and where the one seam is. */
export function FalconPlatformDiagram() {
  return <Canvas d={PLATFORM} mode="contrast" />;
}

/** The detail: the handover loop and the live-update loop, step by step. */
export function FalconLoopsDiagram() {
  return <Canvas d={LOOPS} mode="contrast" />;
}
